import React, { useState } from 'react';
import { Routes, Route } from 'react-router-dom';
import { PermissionProvider, usePermissions } from '../../contexts/PermissionContext';
import AdminSidebar from './AdminSidebar';
import AdminHeader from './AdminHeader';
import DashboardOverview from './DashboardOverview';
import BookingManagement from './BookingManagement';
import AdminCreateBooking from './AdminCreateBooking';
import UserManagement from './UserManagement';
import CourierRateManagement from './CourierRateManagement';
import KYCManagement from './KYCManagement';
import AnalyticsReports from './AnalyticsReports';
import TrackingManagement from './TrackingManagement';
import AdminSettings from './AdminSettings';
import WalletTransactions from './WalletTransactions';
import WalletManagement from './WalletManagement';
import DiscountManagement from './DiscountManagement';

// Typing a URL straight into the bar should not get past the menu. The server
// refuses the data either way; this is so the page says why instead of filling
// with failed requests.
const Guard = ({ p, children }) => {
  const { hasPermission, loading, error, refreshPermissions } = usePermissions();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-gray-500">
        Checking your access...
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-sm text-amber-800">{error}</p>
        <button
          type="button"
          onClick={refreshPermissions}
          className="mt-3 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
        >
          Try again
        </button>
      </div>
    );
  }

  if (!hasPermission(p)) {
    return (
      <div className="mx-auto max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center">
        <h2 className="text-lg font-semibold text-gray-900">You do not have access to this section</h2>
        <p className="mt-2 text-sm text-gray-500">
          Your role does not include this. Ask a super admin if you need it.
        </p>
      </div>
    );
  }

  return children;
};

const AdminDashboard = ({ user, onLogout }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);


  return (
    <PermissionProvider>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar */}
        <AdminSidebar 
          open={sidebarOpen} 
          setOpen={setSidebarOpen}
        />

        {/* Main content */}
        <div className={`flex-1 flex flex-col min-w-0 transition-all duration-300 ${sidebarOpen ? 'ml-64' : 'ml-20'}`}>
          {/* Header */}
          <AdminHeader 
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            user={user}
            onLogout={onLogout}
          />

          {/* Main content area */}
          <main className="flex-1 min-w-0 p-6 overflow-x-hidden overflow-y-auto">
            <Routes>
              <Route index element={<Guard p="view_dashboard"><DashboardOverview /></Guard>} />
              <Route path="bookings" element={<Guard p="view_bookings"><BookingManagement /></Guard>} />
              <Route path="bookings/new" element={<Guard p="create_booking"><AdminCreateBooking /></Guard>} />
              <Route path="users" element={<Guard p="view_customers"><UserManagement /></Guard>} />
              <Route path="wallet" element={<Guard p="view_wallets"><WalletTransactions /></Guard>} />
              <Route path="wallet-management" element={<Guard p="view_wallets"><WalletManagement /></Guard>} />
              <Route path="rates" element={<Guard p="view_rates"><CourierRateManagement /></Guard>} />
              <Route path="kyc" element={<Guard p="view_kyc"><KYCManagement /></Guard>} />
              <Route path="analytics" element={<Guard p="view_analytics"><AnalyticsReports /></Guard>} />
              <Route path="tracking" element={<Guard p="view_tracking"><TrackingManagement /></Guard>} />
              <Route path="settings" element={<Guard p="view_settings"><AdminSettings /></Guard>} />
              <Route path="discounts" element={<Guard p="manage_discounts"><DiscountManagement /></Guard>} />
            </Routes>
          </main>
        </div>
      </div>
    </PermissionProvider>
  );
};

export default AdminDashboard;